import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { CountrySelector, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';

import { isValidPhone, normalizePhone, phoneCountries } from '@/lib/phone';

interface PhoneRowProps {
  value: string;
  onChange: (e164: string) => void;
  onDelete: () => void;
  error: string | null;
  disabled?: boolean;
}

function PhoneRow({ value, onChange, onDelete, error, disabled }: PhoneRowProps): JSX.Element {
  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'ar',
    value,
    countries: phoneCountries,
    onChange: ({ phone }) => onChange(phone),
  });

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <TextField
        size="small"
        fullWidth
        value={inputValue}
        onChange={handlePhoneValueChange}
        inputRef={inputRef}
        disabled={disabled}
        error={!!error}
        helperText={error ?? ' '}
        InputProps={{
          startAdornment: (
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              <CountrySelector
                selectedCountry={country.iso2}
                onSelect={({ iso2 }) => setCountry(iso2)}
                disabled={disabled}
                countries={phoneCountries}
              />
            </Box>
          ),
        }}
      />
      <IconButton
        size="small"
        color="error"
        onClick={onDelete}
        disabled={disabled}
        sx={{ mt: 0.5, flexShrink: 0 }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}


interface NumerosAdicionalesListProps {
  numeros: string[];
  onChange: (numeros: string[]) => void;
  showErrors: boolean;
  disabled?: boolean;
}

export function NumerosAdicionalesList({
  numeros,
  onChange,
  showErrors,
  disabled,
}: NumerosAdicionalesListProps): JSX.Element {
  function handleChange(index: number, e164: string): void {
    onChange(numeros.map((n, i) => (i === index ? e164 : n)));
  }

  function handleDelete(index: number): void {
    onChange(numeros.filter((_, i) => i !== index));
  }

  function handleAdd(): void {
    onChange([...numeros, '']);
  }

  function getError(index: number): string | null {
    if (!showErrors) return null;
    const phone = numeros[index] ?? '';
    if (!phone) return 'Ingresá un número.';
    if (!isValidPhone(phone)) return 'Número inválido. Usá formato internacional.';
    const normalized = normalizePhone(phone);
    const isDuplicate = numeros.some((n, i) => i !== index && !!n && normalizePhone(n) === normalized);
    if (isDuplicate) return 'Número duplicado.';
    return null;
  }

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} mb={1}>
        Números adicionales
      </Typography>

      {numeros.length === 0 && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          Sin números adicionales.
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {numeros.map((num, index) => (
          <PhoneRow
            key={index}
            value={num}
            onChange={(e164) => handleChange(index, e164)}
            onDelete={() => handleDelete(index)}
            error={getError(index)}
            disabled={disabled}
          />
        ))}
      </Box>

      <Button
        size="small"
        onClick={handleAdd}
        disabled={disabled}
        sx={{ mt: 1 }}
      >
        + Agregar número
      </Button>
    </Box>
  );
}

export { isValidPhone as isValidE164 };
